import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {ImageOpPerPixelFn} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {assertSameSizeAndBatchSize} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {
    createImageOpImgProc,
    getImgProcChannelLayoutByCount,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {BlendMode, Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend/definition"
import {assertNever} from "@cm/utils"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"

const nodeDefinition = {...Definition, nodeClass: "ImageOpNodeBlend"}

@registerImageOpNode
export class ImageOpNodeBlend extends ImageOpPerPixelFn<typeof Definition> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof Definition>) {
        super(parameters)
        this.listenToParameterChange("blendMode", "premultipliedAlpha").subscribe(() => this.requestCompositorRebuild())
    }

    protected getCompositingInfo(): HalCompositorInfo {
        const hasSeparateAlpha = this.parameters.alpha !== undefined
        const blendFunc = (mode: BlendMode, premultipliedAlpha: boolean) => {
            switch (mode) {
                case "over":
                    return `background * (1.0 - alpha) + foreground${premultipliedAlpha ? "" : " * alpha"}`
                case "add":
                    return "vec4(background.rgb + foreground.rgb, background.a)"
                case "multiply":
                    return "background * foreground"
                case "darken":
                    return "min(background, foreground)"
                case "lighten":
                    return "max(background, foreground)"
                case "screen":
                    return "background * (1.0 - foreground) + foreground"
                default:
                    assertNever(mode)
            }
        }
        return {
            compositingFunction: `
                vec4 background = fetchSource(0);
                vec4 foreground = fetchSource(1);
                float alpha = ${
                    ImageOpNodeBlend.needsAlphaChannel(this.parameters.blendMode) ? (hasSeparateAlpha ? "fetchSourceChannel(2, 0)" : "foreground.a") : "0.0"
                };
                return ${blendFunc(this.parameters.blendMode, this.parameters.premultipliedAlpha ?? true)};
            `,
            sourceParameterNames: ["backgroundImage", "foregroundImage", "alpha"],
        }
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof Definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const alphaConst = typeof evaluatedParameters.alpha === "number" ? evaluatedParameters.alpha : undefined
        const alphaImage = evaluatedParameters.alpha && typeof evaluatedParameters.alpha !== "number" ? evaluatedParameters.alpha : undefined
        assertSameSizeAndBatchSize(evaluatedParameters.backgroundImage.descriptor, evaluatedParameters.foregroundImage.descriptor)
        if (alphaImage) {
            assertSameSizeAndBatchSize(evaluatedParameters.backgroundImage.descriptor, alphaImage.descriptor)
        }
        let foregroundNode: ImageOpImgProc
        if (ImageOpNodeBlend.needsAlphaChannel(evaluatedParameters.blendMode)) {
            const premultipliedAlpha = evaluatedParameters.premultipliedAlpha ?? true
            let nodeAlpha = alphaImage
            if (!nodeAlpha) {
                if (alphaConst !== undefined) {
                    nodeAlpha = createImageOpImgProc(evaluatedParameters.foregroundImage.descriptor, {
                        type: "math",
                        operation: "constLike",
                        firstInput: evaluatedParameters.foregroundImage.node,
                        secondInput: alphaConst,
                    })
                } else {
                    if (evaluatedParameters.foregroundImage.descriptor.channels !== 4) {
                        throw new Error("Alpha channel neither supplied in foreground image nor in alpha image")
                    }
                    nodeAlpha = createImageOpImgProc(
                        {
                            ...evaluatedParameters.foregroundImage.descriptor,
                            channels: 1,
                        },
                        {
                            type: "extractChannel",
                            input: evaluatedParameters.foregroundImage.node,
                            channel: 3,
                        },
                    )
                }
            }
            if (nodeAlpha.descriptor.channels !== 1) {
                nodeAlpha = createImageOpImgProc(
                    {
                        ...nodeAlpha.descriptor,
                        channels: 1,
                    },
                    {
                        type: "extractChannel",
                        input: nodeAlpha.node,
                        channel: 0,
                    },
                )
            }
            let nodeR = createImageOpImgProc(
                {
                    ...evaluatedParameters.foregroundImage.descriptor,
                    channels: 1,
                },
                {
                    type: "extractChannel",
                    input: evaluatedParameters.foregroundImage.node,
                    channel: 0,
                },
            )
            if (!premultipliedAlpha) {
                nodeR = createImageOpImgProc(nodeR.descriptor, {
                    type: "math",
                    operation: "*",
                    firstInput: nodeR.node,
                    secondInput: nodeAlpha.node,
                })
            }
            if (evaluatedParameters.foregroundImage.descriptor.channels !== 1) {
                let nodeG = createImageOpImgProc(
                    {
                        ...evaluatedParameters.foregroundImage.descriptor,
                        channels: 1,
                    },
                    {
                        type: "extractChannel",
                        input: evaluatedParameters.foregroundImage.node,
                        channel: 1,
                    },
                )
                if (!premultipliedAlpha) {
                    nodeG = createImageOpImgProc(nodeG.descriptor, {
                        type: "math",
                        operation: "*",
                        firstInput: nodeG.node,
                        secondInput: nodeAlpha.node,
                    })
                }
                let nodeB = createImageOpImgProc(
                    {
                        ...evaluatedParameters.foregroundImage.descriptor,
                        channels: 1,
                    },
                    {
                        type: "extractChannel",
                        input: evaluatedParameters.foregroundImage.node,
                        channel: 2,
                    },
                )
                if (!premultipliedAlpha) {
                    nodeB = createImageOpImgProc(nodeB.descriptor, {
                        type: "math",
                        operation: "*",
                        firstInput: nodeB.node,
                        secondInput: nodeAlpha.node,
                    })
                }
                foregroundNode = createImageOpImgProc(
                    {
                        ...evaluatedParameters.foregroundImage.descriptor,
                        channels: 4,
                    },
                    {
                        type: "combineChannels",
                        input: [nodeR.node, nodeG.node, nodeB.node, nodeAlpha.node],
                        channelLayout: "RGBA",
                    },
                )
            } else {
                foregroundNode = createImageOpImgProc(
                    {...evaluatedParameters.foregroundImage.descriptor, channels: 4},
                    {
                        type: "combineChannels",
                        input: [nodeR.node, nodeR.node, nodeR.node, nodeAlpha.node],
                        channelLayout: "RGBA",
                    },
                )
            }
        } else {
            foregroundNode = evaluatedParameters.foregroundImage
        }
        const resultNode = createImageOpImgProc(evaluatedParameters.backgroundImage.descriptor, {
            type: "blend",
            background: evaluatedParameters.backgroundImage.node,
            foreground: foregroundNode.node,
            mode: ImageOpNodeBlend.getImgProcBlendMode(evaluatedParameters.blendMode),
        })
        const descriptor = this.determineDescriptor(evaluatedParameters)
        return createImageOpImgProc(descriptor, {
            type: "convert",
            input: resultNode.node,
            channelLayout: getImgProcChannelLayoutByCount(evaluatedParameters.backgroundImage.descriptor.channels),
            dataType: "float32",
        }) // since the blend node always returns RGBA/float32 we need to convert to backgroundImage's format for consistent behaviour
    }

    private static needsAlphaChannel(mode: BlendMode) {
        return mode === "over"
    }

    private static getImgProcBlendMode(mode: BlendMode): ImageProcessingNodes.Blend["mode"] {
        switch (mode) {
            case "over":
                return "normal"
            default:
                return mode
        }
    }
}
